import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppState } from '../context/AppStateContext';
import { UserContext } from '../context/UserContext';
import CooldownTimer from './CooldownTimer';
import apiConfig from '../config/apiConfig';
import './LeftSidebar.css';

// Define the localStorage key constant
const TRAINING_COOLDOWN_KEY = 'training_cooldown';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    areTheDeedsAvailable, 
    inJail, 
    deedsCooldown, 
    trainingCooldown,
    jailReleaseTime,
    updateDeedsCooldown, 
    updateJailStatus, 
    updateCooldownsFromAPI,
    updateTrainingCooldown, 
    refreshCooldowns 
  } = useAppState();
  const { user, refreshCharacterStats } = useContext(UserContext);
  const [wasOnDeedsPage, setWasOnDeedsPage] = useState(false);
  const [wasOnTrainingPage, setWasOnTrainingPage] = useState(false);
  const [lastCooldownCheck, setLastCooldownCheck] = useState(0);
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
  const [localCooldown, setLocalCooldown] = useState(null); // Add local state for immediate display
  
  // Add these new states to prevent excessive API calls
  const isCheckingCooldownsRef = useRef(false);
  const timerRef = useRef(null);
  const globalCooldownRef = useRef(Date.now());
  
  // Add these for more responsive updates
  const lastUpdateTimeRef = useRef(Date.now());
  const pollingIntervalRef = useRef(null);
  const trainingStatusCheckCount = useRef(0);
  
  // Check if user has a character
  const hasCharacter = user && user.playerCharacter;

  // On component mount, immediately refresh cooldowns from localStorage
  useEffect(() => {
    console.log('Sidebar initializing - refreshing cooldowns');
    // First refresh from localStorage (synchronous)
    refreshCooldowns();
    // Then get latest from API (asynchronous) but with a delay
    setTimeout(() => {
      if (hasCharacter) {
        refreshCharacterStats();
      }
    }, 3000); // Add a 3 second delay to prevent immediate API calls
    return () => {
      // Clear any pending timers on unmount
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [refreshCooldowns, refreshCharacterStats, hasCharacter]); // Add the missing dependencies

  // Calculate deeds availability right away
  const deedsAvailable = areTheDeedsAvailable();

  // Calculate training availability considering both local and global state
  const effectiveTrainingCooldown = localCooldown || trainingCooldown;
  const effectiveTrainingAvailable = (() => {
    if (!effectiveTrainingCooldown) return true;
    const now = new Date();
    return now > effectiveTrainingCooldown;
  })();

  // Debug logging to track state changes
  useEffect(() => {
    console.log('[Sidebar] State update:', { 
      deedsCooldown: deedsCooldown?.toLocaleString() || 'None',
      deedsAvailable,
      trainingCooldown: trainingCooldown?.toLocaleString() || 'None',
      trainingAvailable: effectiveTrainingAvailable,
      inJail: inJail ? 'Yes' : 'No',
      jailReleaseTime: jailReleaseTime?.toLocaleString() || 'None'
    });
  }, [deedsCooldown, deedsAvailable, trainingCooldown, effectiveTrainingAvailable, inJail, jailReleaseTime]);

  // Keep track of when user was on the deeds page
  useEffect(() => {
    if (location.pathname === '/deeds') {
      setWasOnDeedsPage(true);
    } else {
      setWasOnDeedsPage(false);
    }
    if (location.pathname === '/training') {
      setWasOnTrainingPage(true);
    } else {
      setWasOnTrainingPage(false);
    }
  }, [location.pathname]);

  // Only prevent INITIAL navigation to deeds page when on cooldown or in jail
  useEffect(() => {
    if (location.pathname === '/deeds' && !wasOnDeedsPage) {
      // Double check conditions before navigating away
      if ((deedsCooldown && !deedsAvailable) || inJail) {
        console.log('[Sidebar] Preventing access to deeds page:', {
          deedsCooldown: deedsCooldown?.toLocaleString(),
          deedsAvailable,
          inJail
        });
        navigate('/');
      }
    }
    // Similar prevention for training page
    if (location.pathname === '/training' && !wasOnTrainingPage) {
      // Prevent navigation if in jail or on training cooldown
      if ((trainingCooldown && !effectiveTrainingAvailable) || inJail) {
        console.log('[Sidebar] Preventing access to training page:', {
          trainingCooldown: trainingCooldown?.toLocaleString(),
          trainingAvailable: effectiveTrainingAvailable,
          inJail
        });
        navigate('/');
      }
    }
  }, [location.pathname, deedsCooldown, deedsAvailable, trainingCooldown, effectiveTrainingAvailable, inJail, navigate, wasOnDeedsPage, wasOnTrainingPage]);

  // When cooldown completes, update the state
  const handleCooldownComplete = () => {
    console.log('[Sidebar] Cooldown complete, clearing state');
    updateDeedsCooldown(null);
  };

  // When jail time completes, update the state
  const handleJailComplete = () => {
    console.log('[Sidebar] Jail time complete, clearing state');
    updateJailStatus(false);
  };

  // Check if the current path matches the given path
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Get class name for nav item based on active state
  const getNavItemClass = (path) => {
    let className = 'nav-item';
    if (isActive(path)) className += ' active';
    // First check jail status - it overrides everything except City page access
    if (inJail && ['/deeds', '/quests', '/travel', '/training'].includes(path)) {
      className += ' disabled';
      return className;
    }
    // Then check deed-specific cooldown
    if (path === '/deeds' && deedsCooldown && !deedsAvailable) {
      className += ' disabled cooldown-active';
      return className;
    }
    // Check training-specific cooldown
    if (path === '/training' && trainingCooldown && !effectiveTrainingAvailable) {
      className += ' disabled cooldown-active';
      return className;
    }
    return className;
  };

  // Check if user is admin (role 2+)
  const isAdmin = user && user.role >= 2;

  // Function to fetch and check cooldowns from character endpoint
  // Wrap in useCallback to prevent dependency issues
  const checkCharacterCooldowns = useCallback(async () => {
    // Circuit breaker: If already checking or global cooldown is active, skip
    if (isCheckingCooldownsRef.current) {
      console.log('[Sidebar] Already checking cooldowns, skipping duplicate call');
      return;
    }
    // Global app-wide rate limit - max one call every 15 seconds
    const now = Date.now();
    const timeSinceLastGlobalCheck = now - globalCooldownRef.current;
    if (timeSinceLastGlobalCheck < 15000) {
      console.log(`[Sidebar] Global cooldown active (${Math.round((15000 - timeSinceLastGlobalCheck)/1000)}s remaining), skipping`);
      return;
    }
    // Local cooldown check - specific to this component
    const timeSinceLastCheck = now - lastCooldownCheck;
    if (timeSinceLastCheck < 30000) { // Increase to 30 seconds
      console.log(`[Sidebar] Local cooldown active (${Math.round((30000 - timeSinceLastCheck)/1000)}s remaining), skipping`);
      return;
    }
    try {
      // Set flags to prevent concurrent checks
      isCheckingCooldownsRef.current = true;
      globalCooldownRef.current = now;
      console.log('[Sidebar] Checking character endpoint for cooldowns');
      // Set a timeout to release the lock after 10 seconds even if fetch fails
      const lockReleaseTimer = setTimeout(() => {
        if (isCheckingCooldownsRef.current) {
          console.log('[Sidebar] Force-releasing cooldown check lock after timeout');
          isCheckingCooldownsRef.current = false;
        }
      }, 10000);
      const response = await fetch(apiConfig.getCharacter, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      // Clear the safety timer since we got a response
      clearTimeout(lockReleaseTimer);
      if (!response.ok) {
        throw new Error(`Failed to fetch character data: ${response.status}`);
      }
      const characterData = await response.json();
      console.log('[Sidebar] Received character data with cooldowns:', {
        lastDeed: characterData.lastDeed,
        lastTraining: characterData.lastTraining,
        lastJail: characterData.lastJail,
        lastTravelled: characterData.lastTravelled
      });
      // Process cooldowns
      const cooldownData = {};
      // Check deed cooldown
      if (characterData.lastDeed) {
        const lastDeedTime = new Date(characterData.lastDeed);
        // Only apply if it's in the future
        if (lastDeedTime > new Date()) {
          cooldownData.deedsCooldown = characterData.lastDeed;
          console.log('[Sidebar] Found active deed cooldown:', lastDeedTime);
        }
      }
      // Check training cooldown
      if (characterData.lastTraining) {
        const lastTrainingTime = new Date(characterData.lastTraining);
        // Only apply if it's in the future
        if (lastTrainingTime > new Date()) {
          cooldownData.trainingCooldown = characterData.lastTraining;
          console.log('[Sidebar] Found active training cooldown:', lastTrainingTime);
        }
      }
      // Check jail time
      if (characterData.lastJail) {
        const lastJailTime = new Date(characterData.lastJail);
        // Only apply if it's in the future
        if (lastJailTime > new Date()) {
          cooldownData.inJail = true;
          cooldownData.jailReleaseTime = characterData.lastJail;
          console.log('[Sidebar] Found active jail time:', lastJailTime);
        }
      }
      // Update cooldowns in app state if we found any
      if (Object.keys(cooldownData).length > 0) {
        console.log('[Sidebar] Updating cooldowns in app state:', cooldownData);
        updateCooldownsFromAPI(cooldownData);
      }
      // Record that we've checked cooldowns
      setLastCooldownCheck(now);
    } catch (error) {
      console.error('[Sidebar] Error checking character cooldowns:', error);
    } finally {
      // Always release the lock when done, successful or not
      isCheckingCooldownsRef.current = false;
    }
  }, [lastCooldownCheck, updateCooldownsFromAPI]); // Add proper dependencies here

  // Add an effect to check cooldowns when user navigates or when the component mounts
  useEffect(() => {
    if (hasCharacter) {
      // Instead of calling immediately, schedule with delay to avoid cascading calls
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        checkCharacterCooldowns();
      }, 2000); // 2-second delay before checking on navigation change
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [location.pathname, hasCharacter, checkCharacterCooldowns]); // Add missing dependency

  // Add a listener for the custom cooldown event
  useEffect(() => {
    const handleCooldownUpdate = (event) => {
      // Only respond to the event if it's recent
      if (event.detail && event.detail.cooldown && event.detail.timestamp) {
        if (Date.now() - event.detail.timestamp < 10000) {
          console.log('[Sidebar] Training cooldown update detected via event');
          // Instead of calling API, directly use the data from the event
          updateTrainingCooldown(event.detail.cooldown);
        }
      }
    };
    // Listen for the custom event
    window.addEventListener('trainingCooldownUpdated', handleCooldownUpdate);
    // Clean up
    return () => {
      window.removeEventListener('trainingCooldownUpdated', handleCooldownUpdate);
    };
  }, [updateTrainingCooldown]); // Add missing dependency

  // Reduce polling frequency for cooldown updates
  useEffect(() => {
    // Check for global cooldown updates much less frequently
    const checkForCooldownUpdates = () => {
      if (!hasCharacter) return; // Skip if no character
      if (window.lastTrainingCooldown) {
        const { time, timestamp } = window.lastTrainingCooldown;
        // Only process recent updates (within the last 10 seconds)
        if (Date.now() - timestamp < 10000) {
          console.log('[Sidebar] Detected training cooldown via global variable');
          // Apply the cooldown immediately
          updateTrainingCooldown(time);
          // Clear the global variable to avoid processing it again
          window.lastTrainingCooldown = null;
        }
      }
    };
    // Check less frequently (every 3 seconds instead of every 1 second)
    const intervalId = setInterval(checkForCooldownUpdates, 3000);
    // Clean up
    return () => clearInterval(intervalId);
  }, [updateTrainingCooldown, hasCharacter]);

  // Create a link handler that enforces restrictions and checks cooldowns
  const handleLinkClick = (e, path) => {
    // Only check cooldowns on important navigation (to training or deeds pages)
    if (hasCharacter && ['/training', '/deeds'].includes(path)) {
      // Use setTimeout to not block navigation and spread out API calls
      setTimeout(() => {
        // Only check if enough time has passed
        if (Date.now() - lastCooldownCheck > 30000) {
          checkCharacterCooldowns();
        }
      }, 1000);
    }
    // Don't prevent the home link
    if (path === '/' || path === '/city') return true;
    // Prevent jail-restricted paths
    if (inJail && ['/deeds', '/quests', '/travel', '/training'].includes(path)) {
      e.preventDefault();
      return false;
    }
    // Prevent deeds specifically when on cooldown
    if (path === '/deeds' && deedsCooldown && !deedsAvailable) {
      e.preventDefault();
      return false;
    }
    // Prevent training specifically when on cooldown
    if (path === '/training' && trainingCooldown && !effectiveTrainingAvailable) {
      e.preventDefault();
      return false;
    }
    return true;
  };

  // Add a more direct way to check and display cooldowns 
  const ForceRefreshCooldowns = () => {
    const forceRefresh = useRef(false);
    // Force a refresh of training cooldown when the component mounts
    useEffect(() => {
      // Directly check localStorage for the most accurate cooldown information
      const storedTrainingCooldown = localStorage.getItem(TRAINING_COOLDOWN_KEY);
      if (storedTrainingCooldown) {
        try {
          const cooldownTime = new Date(storedTrainingCooldown);
          const now = new Date();
          if (cooldownTime > now) {
            console.log('[Sidebar] ForceRefresh: Found active training cooldown in localStorage');
            updateTrainingCooldown(cooldownTime);
            forceRefresh.current = true;
          }
        } catch (err) {
          console.error('[Sidebar] Error parsing stored training cooldown in ForceRefresh:', err);
        }
      }
      // Also check for global variable set by Training.js
      if (window.lastTrainingCooldown && window.lastTrainingCooldown.time) {
        const { time } = window.lastTrainingCooldown;
        console.log('[Sidebar] ForceRefresh: Found training cooldown in global variable');
        updateTrainingCooldown(time);
        // Clear the global variable since we've processed it
        window.lastTrainingCooldown = null;
        forceRefresh.current = true;
      }
      // Only call API if we didn't find a valid cooldown locally
      if (!forceRefresh.current) {
        console.log('[Sidebar] ForceRefresh: No local cooldown found, checking API');
        checkCharacterCooldowns();
      }
    }, []);
    return null; // This component doesn't render anything
  };

  // NEW: Function to force update component rendering
  const forceUpdate = useCallback(() => {
    console.log('[Sidebar] Forcing update, counter:', forceUpdateCounter);
    setForceUpdateCounter(prev => prev + 1);
  }, [forceUpdateCounter]);

  // NEW: Register a global function that can be called from other components
  useEffect(() => {
    // Expose a function for direct DOM-based updates from Training.js
    window.forceSidebarCooldownUpdate = (type, cooldownDate) => {
      console.log(`[Sidebar] Force update requested for ${type} cooldown:`, cooldownDate);
      if (type === 'training') {
        // Directly update the state
        updateTrainingCooldown(cooldownDate);
        // Force component to re-render
        forceUpdate();
      }
    };
    return () => {
      delete window.forceSidebarCooldownUpdate;
    };
  }, [updateTrainingCooldown, forceUpdate]);

  // Improved event listener for training cooldown updates
  useEffect(() => {
    const handleCooldownUpdate = (event) => {
      // Accept any cooldown update that's less than 5 seconds old
      if (event.detail && event.detail.cooldown) {
        const now = Date.now();
        const eventTime = event.detail.timestamp || now;
        const isPriority = event.detail.priority === 'high';
        // Process recent or high-priority updates immediately
        if (isPriority || now - eventTime < 5000) {
          console.log('[Sidebar] Processing IMMEDIATE training cooldown update');
          updateTrainingCooldown(event.detail.cooldown);
          forceUpdate(); // Force re-render
        }
      }
    };
    // Listen for the custom event on both window and document
    window.addEventListener('trainingCooldownUpdated', handleCooldownUpdate);
    document.addEventListener('trainingCooldownUpdated', handleCooldownUpdate);
    // NEW: Add listener for force update event
    const handleForceUpdate = (event) => {
      console.log('[Sidebar] Force update event received:', event.detail);
      if (event.detail.type === 'training' && event.detail.cooldown) {
        updateTrainingCooldown(event.detail.cooldown);
        forceUpdate();
      }
    };
    window.addEventListener('FORCE_SIDEBAR_UPDATE', handleForceUpdate);
    return () => {
      window.removeEventListener('trainingCooldownUpdated', handleCooldownUpdate);
      document.removeEventListener('trainingCooldownUpdated', handleCooldownUpdate);
      window.removeEventListener('FORCE_SIDEBAR_UPDATE', handleForceUpdate);
    };
  }, [updateTrainingCooldown, forceUpdate]);

  // More aggressive polling specifically after key actions
  useEffect(() => {
    // Check for cooldown immediately on first render and after any forced updates
    const checkRightNow = () => {
      // Directly read from localStorage for maximum speed
      const storedCooldown = localStorage.getItem(TRAINING_COOLDOWN_KEY);
      if (storedCooldown) {
        try {
          const cooldownTime = new Date(storedCooldown);
          if (cooldownTime > new Date()) {
            console.log('[Sidebar] Found training cooldown in localStorage, applying immediately');
            updateTrainingCooldown(cooldownTime);
          }
        } catch (err) {
          console.error('[Sidebar] Error parsing stored cooldown:', err);
        }
      }
    };
    // Run immediately and after short delay (for race conditions)
    checkRightNow();
    const quickCheckTimer = setTimeout(checkRightNow, 100);
    return () => clearTimeout(quickCheckTimer);
  }, [updateTrainingCooldown, forceUpdateCounter]); // Run on forced updates

  // Expose global reload function
  useEffect(() => {
    window.reloadSidebar = () => {
      console.log('[Sidebar] Performing forced reload via global function');
      refreshCooldowns();
      forceUpdate();
    };
    
    return () => {
      delete window.reloadSidebar;
    };
  }, [refreshCooldowns, forceUpdate]);

  // ENHANCED: Ultra-aggressive continuous cooldown check specifically for training
  useEffect(() => {
    // Function to check localStorage directly
    const checkTrainingCooldown = () => {
      try {
        // Increment counter for debugging
        trainingStatusCheckCount.current++;
        
        // Every 10 checks, log the current status
        if (trainingStatusCheckCount.current % 10 === 0) {
          console.log(`[Sidebar] Training status check #${trainingStatusCheckCount.current}`);
        }
        
        // Check localStorage + timestamp for freshness
        const storedCooldown = localStorage.getItem('training_cooldown');
        const storedTimestamp = localStorage.getItem('training_cooldown_timestamp');
        
        if (storedCooldown) {
          const cooldownTime = new Date(storedCooldown);
          const now = new Date();
          
          // Check if cooldown is still active and valid
          if (cooldownTime > now) {
            // If we have a timestamp, check if it's recent (last 60 seconds)
            const isRecentUpdate = storedTimestamp && 
              (Date.now() - parseInt(storedTimestamp)) < 60000;
            
            // If it's a recent update and different from current state, update immediately
            if (isRecentUpdate && 
                (!effectiveTrainingCooldown || 
                 cooldownTime.getTime() !== effectiveTrainingCooldown.getTime())) {
              
              console.log('[Sidebar] DETECTED FRESH TRAINING COOLDOWN:', cooldownTime.toISOString());
              updateTrainingCooldown(cooldownTime);
              setLocalCooldown(cooldownTime);
              forceUpdate();
              return true;
            } 
            // Otherwise just ensure state is consistent
            else if (!effectiveTrainingCooldown) {
              console.log('[Sidebar] Updating missing training cooldown from localStorage');
              updateTrainingCooldown(cooldownTime);
              setLocalCooldown(cooldownTime);
              forceUpdate();
              return true;
            }
          }
        }
        
        // Only check global flags if localStorage check didn't update anything
        if (window.__TRAINING_COOLDOWN_ACTIVE__ && window.__TRAINING_COOLDOWN_TIME__) {
          try {
            const cooldownTime = new Date(window.__TRAINING_COOLDOWN_TIME__);
            const now = new Date();
            
            if (cooldownTime > now && 
                (!effectiveTrainingCooldown || 
                 cooldownTime.getTime() !== effectiveTrainingCooldown.getTime())) {
              
              console.log('[Sidebar] DETECTED TRAINING COOLDOWN FROM GLOBAL FLAG');
              updateTrainingCooldown(cooldownTime);
              setLocalCooldown(cooldownTime);
              forceUpdate();
              return true;
            }
          } catch (err) {
            console.error('[Sidebar] Error processing global cooldown flag:', err);
          }
        }
        
        return false;
      } catch (err) {
        console.error('[Sidebar] Error in aggressive cooldown check:', err);
        return false;
      }
    };
    
    // Run check immediately
    checkTrainingCooldown();
    
    // CHANGED: Use a much less aggressive polling interval - from 100ms to 1000ms
    pollingIntervalRef.current = setInterval(() => {
      checkTrainingCooldown();
    }, 1000); // Changed from 100ms to 1000ms (1 second)
    
    // CHANGED: Reduce the number of additional checks
    // Only check once more after a short delay
    setTimeout(checkTrainingCooldown, 500);
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [updateTrainingCooldown, forceUpdate, effectiveTrainingCooldown, setLocalCooldown]);

  // ENHANCED: Ultra-responsive event listener for critical cooldown updates
  useEffect(() => {
    // Add a circuit breaker to prevent infinite updates
    let lastUpdateTime = 0;
    const UPDATE_THROTTLE = 500; // Minimum 500ms between updates
    
    const handleUltraImmediateUpdate = (event) => {
      // ADDED: Circuit breaker to prevent too-frequent updates
      const now = Date.now();
      if (now - lastUpdateTime < UPDATE_THROTTLE) {
        console.log('[Sidebar] Throttling immediate update - too frequent');
        return;
      }
      lastUpdateTime = now;
      
      console.log('[Sidebar] 🚨 RECEIVED ULTRA-IMMEDIATE TRAINING COOLDOWN UPDATE');
      
      if (event.detail && event.detail.cooldown) {
        try {
          const cooldownDate = new Date(event.detail.cooldown);
          
          // Update both AppState and local state
          updateTrainingCooldown(cooldownDate);
          setLocalCooldown(cooldownDate);
          
          // Force immediate re-render
          forceUpdate();
          
          // Also record the update time
          lastUpdateTimeRef.current = Date.now();
        } catch (err) {
          console.error('[Sidebar] Error processing ultra-immediate update:', err);
        }
      }
    };
    
    // FIXED: Remove document listeners - only listen on window to avoid duplicates
    // Add listeners for all our different event types
    const eventNames = [
      'TRAINING_COOLDOWN_IMMEDIATE',
      'FORCE_IMMEDIATE_COOLDOWN_UPDATE',
      'COOLDOWN_STATE_CHANGED'
    ];
    
    // Register all event listeners
    eventNames.forEach(eventName => {
      window.addEventListener(eventName, handleUltraImmediateUpdate);
    });
    
    return () => {
      // Clean up all event listeners
      eventNames.forEach(eventName => {
        window.removeEventListener(eventName, handleUltraImmediateUpdate);
      });
    };
  }, [updateTrainingCooldown, forceUpdate, setLocalCooldown]);

  // FIXED: Cleanup function for document event listener (was using addEventListener in cleanup)
  useEffect(() => {
    const handleModalCloseUpdate = (event) => {
      // Add circuit breaker to prevent frequent updates
      if (window.__LAST_MODAL_CLOSE_UPDATE && Date.now() - window.__LAST_MODAL_CLOSE_UPDATE < 500) {
        return;
      }
      window.__LAST_MODAL_CLOSE_UPDATE = Date.now();
      
      if (event.detail && event.detail.trigger === 'modal-close' && event.detail.cooldown) {
        console.log('[Sidebar] Detected modal close cooldown update!');
        
        try {
          const cooldownDate = new Date(event.detail.cooldown);
          
          // EMERGENCY TRIPLE UPDATE
          // 1. Update AppState cooldown
          updateTrainingCooldown(cooldownDate);
          
          // 2. Update local state
          setLocalCooldown(cooldownDate);
          
          // 3. Force component re-render IMMEDIATELY
          setForceUpdateCounter(prev => prev + 1000);
          
          // 4. Direct access to element properties
          const trainingLink = document.querySelector('.nav-item[href="/training"]');
          if (trainingLink) {
            trainingLink.classList.add('cooldown-active');
            trainingLink.classList.add('disabled');
            
            // Force cooldown indicator visibility
            const existingIndicator = trainingLink.querySelector('.sidebar-cooldown-indicator');
            if (!existingIndicator) {
              const indicator = document.createElement('div');
              indicator.className = 'sidebar-cooldown-indicator';
              
              const diffMs = cooldownDate - new Date();
              const minutes = Math.floor(diffMs / 60000);
              const seconds = Math.floor((diffMs % 60000) / 1000);
              
              indicator.textContent = `${minutes}m ${seconds}s`;
              trainingLink.appendChild(indicator);
            }
          }
        } catch (err) {
          console.error('[Sidebar] Error handling modal close event:', err);
        }
      }
    };
    
    window.addEventListener('FORCE_IMMEDIATE_COOLDOWN_UPDATE', handleModalCloseUpdate);
    
    return () => {
      window.removeEventListener('FORCE_IMMEDIATE_COOLDOWN_UPDATE', handleModalCloseUpdate);
    };
  }, [updateTrainingCooldown, setLocalCooldown, forceUpdateCounter]);

  // Enhanced sidebar reload function with DOM manipulation
  useEffect(() => {
    window.reloadSidebar = () => {
      console.log('[Sidebar] EMERGENCY SIDEBAR RELOAD');
      
      // 1. Manual localStorage check
      const storedCooldown = localStorage.getItem(TRAINING_COOLDOWN_KEY);
      if (storedCooldown) {
        try {
          const cooldownDate = new Date(storedCooldown);
          if (cooldownDate > new Date()) {
            updateTrainingCooldown(cooldownDate);
            setLocalCooldown(cooldownDate);
          }
        } catch (err) {
          console.error('[Sidebar] Error parsing cooldown in reload:', err);
        }
      }
      
      // 2. Check global variables
      if (window.__TRAINING_COOLDOWN_ACTIVE__ && window.__TRAINING_COOLDOWN_TIME__) {
        try {
          const cooldownDate = new Date(window.__TRAINING_COOLDOWN_TIME__);
          if (cooldownDate > new Date()) {
            updateTrainingCooldown(cooldownDate);
            setLocalCooldown(cooldownDate);
          }
        } catch (err) {
          console.error('[Sidebar] Error parsing global cooldown in reload:', err);
        }
      }
      
      // 3. Super aggressive force re-render
      setForceUpdateCounter(prev => prev + 10000);
      
      // 4. Emergency DOM manipulation for immediate visual update
      const trainingLink = document.querySelector('.nav-item[href="/training"]');
      if (trainingLink && (effectiveTrainingCooldown || trainingCooldown)) {
        trainingLink.classList.add('cooldown-active');
        trainingLink.classList.add('disabled');
        
        const dot = trainingLink.querySelector('.notification-dot');
        if (dot) dot.className = 'notification-dot cooldown';
        
        // Force cooldown indicator visibility
        if (!trainingLink.querySelector('.sidebar-cooldown-indicator') && effectiveTrainingCooldown) {
          const indicator = document.createElement('div');
          indicator.className = 'sidebar-cooldown-indicator';
          
          const diffMs = effectiveTrainingCooldown - new Date();
          const minutes = Math.floor(diffMs / 60000);
          const seconds = Math.floor((diffMs % 60000) / 1000);
          
          indicator.textContent = `${minutes}m ${seconds}s`;
          trainingLink.appendChild(indicator);
        }
      }
      
      return true;
    };
    
    return () => {
      delete window.reloadSidebar;
    };
  }, [updateTrainingCooldown, setLocalCooldown, forceUpdateCounter, trainingCooldown, effectiveTrainingCooldown]);

  // Add a global emergency function to update sidebar
  useEffect(() => {
    window.EMERGENCY_UPDATE_TRAINING_COOLDOWN = (cooldownDate) => {
      console.log('🚨 EMERGENCY SIDEBAR UPDATE INITIATED!');
      
      try {
        // 1. If we have a date string, convert it to a Date object
        const date = typeof cooldownDate === 'string' ? new Date(cooldownDate) : cooldownDate;
        
        // 2. Super aggressive DOM update even before React renders
        const trainingLink = document.querySelector('.nav-item[href="/training"]');
        if (trainingLink) {
          // Add classes directly
          trainingLink.classList.add('disabled', 'cooldown-active');
          
          // Force visible indicator
          let indicator = trainingLink.querySelector('.sidebar-cooldown-indicator');
          if (!indicator) {
            indicator = document.createElement('div');
            indicator.className = 'sidebar-cooldown-indicator force-visible';
            trainingLink.appendChild(indicator);
          }
          
          // Calculate time remaining
          const diffMs = date - new Date();
          if (diffMs > 0) {
            const minutes = Math.floor(diffMs / 60000);
            const seconds = Math.floor((diffMs % 60000) / 1000);
            indicator.textContent = `${minutes}m ${seconds}s`;
            indicator.style.backgroundColor = 'rgba(255, 152, 0, 0.8)';
            indicator.style.color = 'white';
            indicator.style.fontWeight = 'bold';
          }
          
          // Update notification dot
          const dot = trainingLink.querySelector('.notification-dot');
          if (dot) dot.className = 'notification-dot cooldown';
        }
        
        // 3. Update state for React to catch up
        updateTrainingCooldown(date);
        setLocalCooldown(date);
        setForceUpdateCounter(prev => prev + 10000);
      } catch (err) {
        console.error('Error in emergency training cooldown update:', err);
      }
    };
    
    return () => {
      delete window.EMERGENCY_UPDATE_TRAINING_COOLDOWN;
    };
  }, [updateTrainingCooldown, setLocalCooldown, setForceUpdateCounter]);

  // Render logic - Include debug info in the DOM
  return (
    <div className="sidebar">
      {/* Debug counter to track updates */}
      <div style={{ display: 'none' }} data-update-counter={forceUpdateCounter} />
      
      {/* Simple hidden debug display */}
      <div style={{ display: 'none' }}>
        Training cooldown: {effectiveTrainingCooldown?.toISOString() || 'None'}
        Available: {effectiveTrainingAvailable ? 'Yes' : 'No'}
        Update count: {forceUpdateCounter}
      </div>
      
      {/* Add the force refresh component */}
      <ForceRefreshCooldowns />
      
      <nav className="nav-menu">
        {/* Home nav item - always visible */}
        <Link to="/" className={getNavItemClass('/')} onClick={(e) => handleLinkClick(e, '/')}>
          <span className="icon">🏠</span>
          <span className="text">Home</span>
        </Link>
        
        {/* Only show the rest of nav items if user is logged in and has a character */}
        {hasCharacter && (
          <>
            <Link 
              to="/deeds" 
              className={getNavItemClass('/deeds')}
              onClick={(e) => handleLinkClick(e, '/deeds')}
              title={deedsCooldown && !deedsAvailable ? "Deeds on cooldown" : "Deeds available"}
            >
              <span className="icon">📝</span>
              <span className="text">Deeds</span>
              {deedsAvailable ? (
                <span className="notification-dot available"></span>
              ) : (
                <span className="notification-dot cooldown"></span>
              )}
              {/* Force cooldown timer to show when deedsCooldown exists AND is in the future */}
              {deedsCooldown && (() => {
                // Get times and calculate difference in seconds
                const now = new Date();
                const cooldownTime = new Date(deedsCooldown);
                const diffSeconds = (cooldownTime.getTime() - now.getTime()) / 1000;
                
                // Debug the comparison
                console.log('[Sidebar] Cooldown check:', {
                  now: now.toISOString(),
                  cooldownTime: cooldownTime.toISOString(),
                  diffSeconds,
                  shouldShow: diffSeconds > -1 // Show if within 1 second of now (even if barely expired)
                });
                
                // Show timer if cooldown is still active (or just barely expired)
                if (diffSeconds > -1) {
                  return (
                    <div className="sidebar-cooldown-indicator">
                      <CooldownTimer 
                        targetTime={deedsCooldown} 
                        onComplete={handleCooldownComplete}
                        showPrefix={false}
                      />
                    </div>
                  );
                }
                return null;
              })()}
            </Link>
            
            <Link 
              to="/training" 
              className={getNavItemClass('/training')}
              onClick={(e) => handleLinkClick(e, '/training')}
              title={effectiveTrainingCooldown && !effectiveTrainingAvailable ? "Training on cooldown" : "Training available"}
              data-cooldown={effectiveTrainingCooldown ? 'active' : 'inactive'} // Add data attribute for debugging
            >
              <span className="icon">🏋️</span>
              <span className="text">Training</span>
              {effectiveTrainingAvailable ? (
                <span className="notification-dot available"></span>
              ) : (
                <span className="notification-dot cooldown"></span>
              )}
              
              {/* Use immediate cooldown display component */}
              <ImmediateTrainingCooldown 
                cooldown={effectiveTrainingCooldown}
                available={effectiveTrainingAvailable}
                updateCounter={forceUpdateCounter}
                onComplete={() => {
                  updateTrainingCooldown(null);
                  setLocalCooldown(null);
                  refreshCharacterStats(true);
                  forceUpdate();
                }}
              />
            </Link>
            
            <Link 
              to="/quests" 
              className={getNavItemClass('/quests')}
              onClick={(e) => handleLinkClick(e, '/quests')}
            >
              <span className="icon">🗺️</span>
              <span className="text">Quests</span>
            </Link>
            
            <Link 
              to="/city" 
              className={getNavItemClass('/city')}
              onClick={(e) => handleLinkClick(e, '/city')}
              title={inJail ? "You can visit the jail in the city" : "Visit city"}
            >
              <span className="icon">🏙️</span>
              <span className="text">City</span>
            </Link>
            
            <Link 
              to="/travel" 
              className={getNavItemClass('/travel')}
              onClick={(e) => handleLinkClick(e, '/travel')}
            >
              <span className="icon">🗺️</span>
              <span className="text">Travel</span>
            </Link>
            
            <Link to="/chat" className={getNavItemClass('/chat')} onClick={(e) => handleLinkClick(e, '/chat')}>
              <span className="icon">💬</span>
              <span className="text">Chat</span>
            </Link>
            
            <Link to="/character/search" className={getNavItemClass('/character/search')} onClick={(e) => handleLinkClick(e, '/character/search')}>
              <span className="icon">🔍</span>
              <span className="text">Search Players</span>
            </Link>
            
            <Link to="/profile" className={getNavItemClass('/profile')} onClick={(e) => handleLinkClick(e, '/profile')}>
              <span className="icon">👤</span>
              <span className="text">Profile</span>
            </Link>

            {isAdmin && (
              <>
                <div className="sidebar-spacer"></div>
                <Link to="/admin" className={`${getNavItemClass('/admin')} admin-item`} onClick={(e) => handleLinkClick(e, '/admin')}>
                  <span className="icon">⚙️</span>
                  <span className="text">Admin Dashboard</span>
                </Link>
              </>
            )}

            {inJail && (
              <div className="jail-notice">
                <span className="jail-icon">⛓️</span>
                <span className="jail-text">
                  You are in jail
                  {jailReleaseTime && (
                    <>
                      <br />
                      Release in: <CooldownTimer 
                        targetTime={jailReleaseTime} 
                        onComplete={handleJailComplete}
                        showPrefix={false} 
                      />
                    </>
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </nav>
    </div>
  );
};

// NEW: More immediate cooldown display component with streamlined rendering
const ImmediateTrainingCooldown = ({ cooldown, available, updateCounter, onComplete }) => {
  const [displayTime, setDisplayTime] = useState('');
  const timerRef = useRef(null);
  
  // Use useEffect instead of useLayoutEffect for timer functionality
  useEffect(() => {
    // Skip effect logic if no cooldown or available
    if (!cooldown || available) return;
    
    // Clear any existing interval
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Calculate and update the display time
    const updateDisplay = () => {
      try {
        const now = new Date();
        const cooldownDate = new Date(cooldown);
        const diffMs = cooldownDate - now;
        
        if (diffMs <= 0) {
          setDisplayTime('Available now');
          if (onComplete) onComplete();
          clearInterval(timerRef.current);
          return;
        }
        
        const minutes = Math.floor(diffMs / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        setDisplayTime(`${minutes}m ${seconds}s`);
        
        // FORCE IMMEDIATE VISIBILITY - update DOM directly in worst case
        setTimeout(() => {
          const indicator = document.querySelector('.sidebar-cooldown-indicator');
          if (indicator && !indicator.textContent) {
            indicator.textContent = `${minutes}m ${seconds}s`;
          }
        }, 50);
      } catch (err) {
        console.error('[ImmediateTrainingCooldown] Timer error:', err);
        setDisplayTime('Error');
      }
    };
    
    // Run immediately
    updateDisplay();
    
    // Set up the interval timer to update EVERY SECOND for precise countdown
    timerRef.current = setInterval(updateDisplay, 1000);
    
    // Clean up on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [cooldown, onComplete, updateCounter, available]); // Include all dependencies
  
  // Return early AFTER defining hooks if no display needed
  if (!cooldown || available) return null;
  
  return (
    <div className="sidebar-cooldown-indicator" data-update-counter={updateCounter}>
      {displayTime}
    </div>
  );
};

export default Sidebar;